import along from "@turf/along";

import bearing from "@turf/bearing";
import { lineString } from "@turf/helpers";
import length from "@turf/length";
import { first, last } from "lodash";
import { MercatorCoordinate } from "mapbox-gl";
import { Easing, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { computeCameraPosition } from "../computeCameraPosition";
import { interpolateAltitude, legHeadingAt } from "./legHeading";
import { writeStopMarker } from "./markerWrites";
import { CITY_LEG_KM, CITY_MAX_ALTITUDE } from "../reelConstants";

// Helper to validate coordinates
const isValidCoord = (val: any): val is number => typeof val === "number" && !isNaN(val) && isFinite(val);

// Helper to get valid coordinate pair, with fallback
const getValidCoordPair = (lng: any, lat: any, fallbackLng = 0, fallbackLat = 0): [number, number] => {
	return [isValidCoord(lng) ? lng : fallbackLng, isValidCoord(lat) ? lat : fallbackLat];
};

// Safe bearing calculation that handles invalid coordinates
const safeBearing = (from: any, to: any): number => {
	try {
		if (!from || !to) return 0;
		const fromCoord = Array.isArray(from) ? from : [from?.lng ?? from?.[0], from?.lat ?? from?.[1]];
		const toCoord = Array.isArray(to) ? to : [to?.lng ?? to?.[0], to?.lat ?? to?.[1]];

		if (!isValidCoord(fromCoord[0]) || !isValidCoord(fromCoord[1]) || !isValidCoord(toCoord[0]) || !isValidCoord(toCoord[1])) {
			return 0;
		}
		return bearing(fromCoord, toCoord);
	} catch {
		return 0;
	}
};

const MarkerSequence = ({
	map,
	index,
	MARKER_DURATION,
	currentTrip,
	reelData,
	startAltitude,
	endAltitude,
	startPitch,
	showPhotoCards = false,
	cardStagger = 5,
	cardStiffness = 260,
	scale,
}) => {
	const markerFrame = useCurrentFrame();
	const markerPhase = (markerFrame + 1) / MARKER_DURATION;
	const { fps } = useVideoConfig();

	if (!map) return null;

	// Where this stop actually is.
	//
	// This used to clamp the index into `currentTrip.destinations`, which quietly turns a trip whose
	// two lists have drifted apart into a marker standing somewhere else entirely -- and since the
	// camera walks from the previous leg's end, through the marker, to the next leg's start, "somewhere
	// else" means it flies there and back inside the one second the stop lasts. Caught on a Koh Phangan
	// trip with ten legs and eight destinations: the stop sent the camera to Los Angeles and back, at
	// 9,637 m, which renders as an empty ocean and reads as the map having broken.
	//
	// The legs always know where their own ends are, so trust the geometry and take the named
	// destination only when the two lists line up.
	const aligned = (currentTrip?.destinations?.length ?? 0) === (reelData?.length ?? 0);
	const currentDest = aligned ? currentTrip?.destinations?.[index] : undefined;
	const legEnd = (first(reelData?.[index]?.path) ?? last(reelData?.[index - 1]?.path)) as number[] | undefined;
	const destLng = currentDest?.longitude ?? currentDest?.lng ?? legEnd?.[0] ?? reelData?.[index]?.lng ?? 0;
	const destLat = currentDest?.latitude ?? currentDest?.lat ?? legEnd?.[1] ?? reelData?.[index]?.lat ?? 0;
	const markerLngLat = getValidCoordPair(destLng, destLat);

	// Safely get path data with fallbacks
	const currentPath = reelData?.[index]?.path;
	const prevPath = reelData?.[index - 1]?.path;

	// Create safe lineString for nextPath
	let nextPath;
	try {
		if (index < (reelData?.length ?? 0) - 1 && Array.isArray(currentPath) && currentPath.length >= 2) {
			// Validate all coordinates in path
			const validPath = currentPath.filter((coord: any) => Array.isArray(coord) && coord.length >= 2 && isValidCoord(coord[0]) && isValidCoord(coord[1]));
			if (validPath.length >= 2) {
				nextPath = lineString(validPath);
			}
		}
	} catch {
		nextPath = undefined;
	}

	const nextPathDistance = nextPath ? length(nextPath) : 200;
	// Same scaling as the path altitude in TikTokVideoVanilla: a 3 km hop should not pull the camera
	// 50 km up just because it is under the 100 km threshold.
	const endEltitude =
		nextPathDistance < CITY_LEG_KM
			? Math.max(1500, Math.min(CITY_MAX_ALTITUDE, 1000 + nextPathDistance * 400))
			: nextPathDistance < 100
				? Math.max(2000, Math.min(50000, nextPathDistance * 1200))
				: 250000;
	// Both boundaries have to match, or the stop reads as a jump. It arrives at the height the last
	// leg was flown at and leaves at the height the next one wants -- measured on the island loop,
	// holding one of them meant an instant 3,580 m change at the busiest stop. Eased, so what is left
	// of the change happens gently rather than at the edges.
	const targetAltitude = typeof endAltitude === "number" && endAltitude > 0 ? endAltitude : startAltitude;
	const altitude = interpolateAltitude(interpolate(markerPhase, [0, 1], [0, 1], { easing: Easing.inOut(Easing.ease) }), startAltitude, targetAltitude);

	// Safely get previous path end coordinates
	const prevPathEnd =
		index > 0 && Array.isArray(prevPath) && prevPath.length > 0
			? getValidCoordPair(last(prevPath)?.[0], last(prevPath)?.[1], markerLngLat[0], markerLngLat[1])
			: markerLngLat;

	// Safely get next path start coordinates
	const nextPathStart =
		index < (reelData?.length ?? 0) - 1 && Array.isArray(currentPath) && currentPath.length > 0
			? getValidCoordPair(currentPath[0]?.[0], currentPath[0]?.[1], prevPathEnd[0], prevPathEnd[1])
			: prevPathEnd;

	// Create camera path with validated coordinates
	let cameraPath;
	let alongPath = markerLngLat;
	try {
		cameraPath = lineString([prevPathEnd, markerLngLat, nextPathStart]);
		const cameraDistance = length(cameraPath);
		alongPath = along(cameraPath, cameraDistance * markerPhase).geometry.coordinates as [number, number];
	} catch {
		// Fallback to marker position if path creation fails
		alongPath = markerLngLat;
	}

	// Use safe bearing calculations
	const pathFirst = first(currentPath);
	const pathLast = last(currentPath);
	// The path sequences on either side of this stop face along the leg. This one still divided the
	// bearing by three, so the camera snapped a long way round on arrival and snapped back on
	// departure. Both ends read the same path, so holding the full bearing makes the stop continuous.
	// Both ends read the same function the path sequences use, at the phase where they hand over: the
	// arriving leg at its very end, the departing leg at its very start. That makes the stop begin
	// pointing exactly where the flight before it stopped, and end pointing exactly where the next one
	// picks up.
	const arriveHeading = index > 0 ? legHeadingAt(reelData?.[index - 1]?.path, 1) : null;
	const departHeading = currentPath?.length >= 2 ? legHeadingAt(currentPath, 0) : null;
	const startHeading = arriveHeading ?? departHeading ?? 0;

	// The stop no longer turns. On a mixed trip the turn can be enormous -- 150 degrees between
	// arriving at Koh Phangan from the north and leaving for Los Angeles across the Pacific -- and
	// doing that in one second while also climbing 19x is what read as a whip-pan. The next leg eases
	// into its own heading over its first third instead, where there is time for it.
	const endHeading = startHeading;

	// Shortest way round, same reason as in PathSequence: 350 to 10 is 20 degrees, not 340.
	const headingDelta = ((endHeading - startHeading + 540) % 360) - 180;
	// Eased, because the whole turn now happens inside one second of stop. Linear made a 100 degree
	// swing start and stop abruptly; easing in and out lets it settle.
	const heading = interpolate(markerPhase, [0, 1], [startHeading, startHeading + headingDelta], {
		easing: Easing.inOut(Easing.ease),
	});

	const lngLat = {
		lng: alongPath[0],
		lat: alongPath[1],
	};

	// compute corrected camera ground position, so that he leading edge of the path is in view
	const correctedPosition = computeCameraPosition(startPitch, heading, lngLat, altitude);

	// set the pitch and bearing of the camera
	const camera = map.getFreeCameraOptions();
	camera.setPitchBearing(startPitch, heading);

	// set the position and altitude of the camera
	camera.position = MercatorCoordinate.fromLngLat(correctedPosition, altitude);

	// apply the new camera options
	map.setFreeCameraOptions(camera);

	// The stop's pin, its number and name, the pulse and the photo fan. FinishedSequences makes the same
	// writes for the last frame of a stop a render tab started after (markerWrites.ts), so a tab that
	// never ran this one leaves the stop in the state a tab that did would.
	writeStopMarker(map, { index, markerFrame, markerDuration: MARKER_DURATION, fps, reelData, showPhotoCards, cardStagger, cardStiffness, scale });

	return <></>;
};

export default MarkerSequence;
